import { waitForConnection } from '@/utils/wagmi/wait-for-connection'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { useCallback, useEffect, useRef } from 'react'
import { useAccount } from 'wagmi'

/**
 * Hook to require wallet connection before executing an action
 *
 * @param action - The action to execute
 */
export function useRequireWalletConnection<T extends (...args: any[]) => ReturnType<T>>(action: T) {
	const { isConnected, isConnecting } = useAccount()
	const { openConnectModal, connectModalOpen } = useConnectModal()
	const pendingAbortController = useRef<AbortController | null>(null)

	useEffect(() => {
		// If the user aborts the connection, we should abort the action as well
		if (!isConnected && !isConnecting && !connectModalOpen && pendingAbortController.current) {
			pendingAbortController.current?.abort()
		}
	}, [isConnected, connectModalOpen, isConnecting])

	// Cleanup the pending abort controller on unmount
	useEffect(() => {
		return () => {
			pendingAbortController.current?.abort()
		}
	}, [])

	return useCallback(
		async (...args: Parameters<T>) => {
			if (isConnected) {
				return action(...args)
			}

			openConnectModal?.()

			const abortController = new AbortController()
			pendingAbortController.current = abortController

			await waitForConnection(abortController.signal)
			pendingAbortController.current = null

			return action(...args)
		},
		[action, isConnected, openConnectModal]
	)
}
